import { toolsApi } from '@/api/tools.api';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { PredictToolsDialog } from '@/components/dialogs/PredictToolsDialog';
import { useHolders } from '@/hooks/useHolders';
import { useTools } from '@/hooks/useTools';
import type {
  CreateHolder,
  CreateTool,
  HolderModel,
  ToolModel,
  ToolType,
  UpdateHolder,
  UpdateTool,
} from '@/types/tool.types';
import { TOOL_TYPE_VALUES } from '@/types/tool.types';
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, Filter, GripVertical, Info, Loader2, Plus, Trash2, X } from 'lucide-react';
import type { DragEvent as ReactDragEvent, FC, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

/** Pending edits per tool id. Only changed fields are sent on submit. */
type EditsMap = Record<string, Partial<UpdateTool>>;

/** Exact-value filters: click on a value to filter, click again to clear. */
type FilterValues = Partial<Record<keyof UpdateTool, string | number | boolean>>;

type EditCell = { toolId: string; field: keyof UpdateTool } | null;

const COLUMNS: { key: keyof UpdateTool; labelKey: string; labelKey2?: string }[] = [
  { key: 'tool_name', labelKey: 'toolManagement.toolName' },
  { key: 'tool_type', labelKey: 'toolManagement.toolType' },
  { key: 'holder_id', labelKey: 'toolManagement.holderName' },
  { key: 'tool_number', labelKey: 'toolManagement.toolNumberLine1', labelKey2: 'toolManagement.toolNumberLine2' },
  { key: 'tool_diameter', labelKey: 'toolManagement.toolDiameterLine1', labelKey2: 'toolManagement.toolDiameterLine2' },
  { key: 'tool_comment', labelKey: 'toolManagement.toolComment' },
  {
    key: 'tool_overall_len',
    labelKey: 'toolManagement.toolOverallLenLine1',
    labelKey2: 'toolManagement.toolOverallLenLine2',
  },
  {
    key: 'non_cutting_diameter',
    labelKey: 'toolManagement.nonCuttingDiameterLine1',
    labelKey2: 'toolManagement.nonCuttingDiameterLine2',
  },
  { key: 'tool_len', labelKey: 'toolManagement.toolLenLine1', labelKey2: 'toolManagement.toolLenLine2' },
  { key: 'tool_rotation', labelKey: 'toolManagement.toolRotation' },
  { key: 'spindle', labelKey: 'toolManagement.spindle' },
  {
    key: 'feed_mach_value',
    labelKey: 'toolManagement.feedMachValueLine1',
    labelKey2: 'toolManagement.feedMachValueLine2',
  },
  {
    key: 'support_finish',
    labelKey: 'toolManagement.supportFinishLine1',
    labelKey2: 'toolManagement.supportFinishLine2',
  },
];

const LS_COL_WIDTHS_KEY = 'toolManagement.colWidths';

/** Default column widths in px — tool_name is wider for longer names. */
const DEFAULT_COL_WIDTHS: Record<string, number> = {
  tool_name: 210,
  tool_type: 150,
  holder_id: 140,
  tool_number: 95,
  tool_diameter: 90,
  tool_comment: 155,
  tool_overall_len: 105,
  non_cutting_diameter: 110,
  tool_len: 85,
  tool_rotation: 85,
  spindle: 80,
  feed_mach_value: 110,
  support_finish: 100,
  _actions: 90,
};

function loadColWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(LS_COL_WIDTHS_KEY);
    if (stored) return { ...DEFAULT_COL_WIDTHS, ...JSON.parse(stored) };
  } catch {
    // ignore
  }
  return { ...DEFAULT_COL_WIDTHS };
}

const LS_COL_ORDER_KEY = 'toolManagement.colOrder';
const DEFAULT_COL_ORDER = COLUMNS.map((c) => c.key);

function loadColOrder(): (keyof UpdateTool)[] {
  try {
    const stored = localStorage.getItem(LS_COL_ORDER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      const defaultSet = new Set<string>(DEFAULT_COL_ORDER);
      if (
        parsed.length === DEFAULT_COL_ORDER.length &&
        parsed.every((k) => defaultSet.has(k))
      ) {
        return parsed as (keyof UpdateTool)[];
      }
    }
  } catch {
    // ignore
  }
  return [...DEFAULT_COL_ORDER];
}

/** Dialog pt aligned with dropdown panel style: border, shadow, rounded, content padding. */
const TOOL_MANAGEMENT_DIALOG_PT = {
  root: {
    className: 'border border-gray-200 rounded-lg shadow-lg dark:border-gray-700 max-h-[90%] transform scale-100 m-0',
  },
  header: {
    className:
      'flex items-center justify-between shrink-0 bg-white text-gray-800 border-b border-gray-200 rounded-tl-lg rounded-tr-lg p-6 dark:bg-gray-900 dark:text-white/80 dark:border-gray-700',
  },
  content: {
    className: 'overflow-y-auto bg-white text-gray-700 px-6 py-4 dark:bg-gray-900 dark:text-white/80',
  },
  footer: {
    className:
      'flex gap-2 shrink-0 justify-end align-center border-t border-gray-200 bg-white text-gray-700 px-6 pb-6 text-right rounded-b-lg dark:bg-gray-900 dark:text-white/80 dark:border-gray-700',
  },
};

function getCellValue<T>(tool: ToolModel, toolId: string, field: keyof UpdateTool, edits: EditsMap): T {
  const e = edits[toolId];
  if (e && field in e && (e[field] !== undefined || e[field] === null)) {
    return e[field] as T;
  }
  return (tool as unknown as Record<string, unknown>)[field] as T;
}

function valuesEqual(a: string | number | boolean, b: string | number | boolean): boolean {
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b;
  return String(a) === String(b);
}

function blockInvalidToolNumberKeys(e: ReactKeyboardEvent<HTMLInputElement>): void {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return;
  }

  const allowedNavigationKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);

  if (allowedNavigationKeys.has(e.key)) {
    return;
  }

  if (e.key.length !== 1) {
    return;
  }

  if (/^[a-zA-Z0-9]$/.test(e.key)) {
    return;
  }

  if (['/', '-', '.', ',', ';'].includes(e.key)) {
    return;
  }

  e.preventDefault();
}

function keepOnlyAllowedToolNumberChars(value: string): string {
  return value.replace(/[^a-zA-Z0-9/.,;-]/g, '');
}

function blockInvalidNumberKeys(e: ReactKeyboardEvent<HTMLInputElement>): void {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return;
  }

  const allowedNavigationKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);

  if (allowedNavigationKeys.has(e.key)) {
    return;
  }

  if (e.key.length !== 1) {
    return;
  }

  const input = e.currentTarget;
  const value = input.value;
  const hasSelection = input.selectionStart !== input.selectionEnd;

  // Accept digits unconditionally.
  if (/[0-9]/.test(e.key)) {
    return;
  }

  // Accept one decimal separator.
  if (e.key === '.' || e.key === ',') {
    if (value.includes('.') || value.includes(',')) {
      e.preventDefault();
    }
    return;
  }

  // Accept minus sign only at the start and only once.
  if (e.key === '-') {
    const cursorAtStart = input.selectionStart === 0;
    if (!cursorAtStart || (value.includes('-') && !hasSelection)) {
      e.preventDefault();
    }
    return;
  }

  e.preventDefault();
}

type BallTypeDropdownValue = 'unset' | 'true' | 'false';

function ballTypeToDropdown(v: boolean | null | undefined): BallTypeDropdownValue {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return 'unset';
}

function getHolderDisplayName(tool: ToolModel, toolId: string, edits: EditsMap, holders: HolderModel[]): string {
  const editedId = edits[toolId]?.holder_id;
  if (editedId) {
    const h = holders.find((x) => x.id === editedId);
    return h?.holder_name ?? editedId;
  }
  return tool.holder?.holder_name ?? '-';
}

const DEFAULT_ADD_TOOL_FORM: CreateTool = {
  // Basic
  tool_name: '',
  tool_type: 'MfgDrillTool',
  tool_comment: '',
  holder_id: '',
  // Core dimensions
  tool_number: 0,
  tool_diameter: 0,
  tool_overall_len: 0,
  tool_len: 0,
  non_cutting_diameter: 0,
  // Optional dimensions
  tool_corner_rad: null,
  tool_cut_angle: null,
  tool_cut_len: null,
  // Rotation
  tool_rotation: 'CLW',
  // Thread / advanced geometry (backend default -1.0 = not applicable)
  pitch: -1,
  tool_pitch: -1,
  length_1: -1,
  thread_diameter: -1,
  diameter: -1,
  // Angle geometry
  horizontal_distance: null,
  vertical_distance: null,
  tip_angle: null,
  flank_angle: null,
  // Machine parameters
  spindle: 0,
  feed_mach_value: 20,
  support_finish: true,
  thread_depth: -1,
  ball_type: null,
};

export const ToolManagement: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    tools,
    isLoading,
    error,
    refetch,
    createTool,
    updateTool,
    deleteTool,
    page,
    setPage,
    pageSize,
    hasNextPage,
    hasPrevPage,
    totalPages,
    totalCount,
    listFilter,
    setListFilter,
  } = useTools();
  const {
    holders,
    isLoading: holdersLoading,
    addHolder,
    updateHolder,
    deleteHolder,
    refetch: refetchHolders,
  } = useHolders();
  const [edits, setEdits] = useState<EditsMap>({});
  /** Latest `edits` for submit — avoids stale `useCallback` closure and matches state after child updates. */
  const editsRef = useRef<EditsMap>({});
  editsRef.current = edits;
  const [submitting, setSubmitting] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [editingCell, setEditingCell] = useState<EditCell>(null);
  const [showAddHolderDialog, setShowAddHolderDialog] = useState(false);
  const [showDimensionsModal, setShowDimensionsModal] = useState(false);
  const [addHolderForm, setAddHolderForm] = useState<CreateHolder>({
    holder_name: '',
    diameter_mm_1: 0,
    diameter_mm_2: 0,
    len_mm_1: 0,
    len_mm_2: 0,
  });
  const [addingHolder, setAddingHolder] = useState(false);
  const [showAddToolDialog, setShowAddToolDialog] = useState(false);
  const [showPredictDialog, setShowPredictDialog] = useState(false);
  const [addToolForm, setAddToolForm] = useState<CreateTool>(DEFAULT_ADD_TOOL_FORM);
  const [addingTool, setAddingTool] = useState(false);
  const [showToolDetailDialog, setShowToolDetailDialog] = useState(false);
  const [detailTool, setDetailTool] = useState<ToolModel | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEdits, setDetailEdits] = useState<Partial<UpdateTool>>({});
  const [detailEditingField, setDetailEditingField] = useState<keyof UpdateTool | null>(null);
  const [detailSaving, setDetailSaving] = useState(false);
  type HolderEditCell = { holderId: string; field: keyof UpdateHolder };
  const [editingHolderCell, setEditingHolderCell] = useState<HolderEditCell | null>(null);
  const [holderEditValue, setHolderEditValue] = useState<string | number | null>(null);
  /** Tool id for which holder update is in progress (show loading in holder cell). */
  const [holderUpdatingToolId, setHolderUpdatingToolId] = useState<string | null>(null);
  /** Tool id for which tool_type update is in progress. */
  const [toolTypeUpdatingToolId, setToolTypeUpdatingToolId] = useState<string | null>(null);
  const filterClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterClickCellRef = useRef<{ toolId: string; field: keyof UpdateTool } | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [colWidths, setColWidths] = useState<Record<string, number>>(loadColWidths);
  const colWidthsRef = useRef(colWidths);
  colWidthsRef.current = colWidths;
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);
  const [colOrder, setColOrder] = useState<(keyof UpdateTool)[]>(loadColOrder);
  const [dragOverKey, setDragOverKey] = useState<keyof UpdateTool | null>(null);
  const [dragSourceKey, setDragSourceKey] = useState<keyof UpdateTool | null>(null);
  const dragStartKeyRef = useRef<keyof UpdateTool | null>(null);
  const [filterDraftHolderIds, setFilterDraftHolderIds] = useState<string[]>([]);
  const [filterDraftToolTypes, setFilterDraftToolTypes] = useState<string[]>([]);
  const [filterDraftToolNumber, setFilterDraftToolNumber] = useState<number | null>(null);
  const [filterDraftOrder, setFilterDraftOrder] = useState<'asc' | 'desc'>('asc');

  const listFilterRef = useRef(listFilter);
  listFilterRef.current = listFilter;
  const holdersRef = useRef(holders);
  holdersRef.current = holders;
  const toolsRef = useRef(tools);
  toolsRef.current = tools;

  const orderedColumns = useMemo(
    () => colOrder.map((key) => COLUMNS.find((c) => c.key === key)!),
    [colOrder]
  );

  useEffect(() => {
    if (!showFilterModal) return;
    const filter = listFilterRef.current;
    const allHolderIds = holdersRef.current.map((h) => h.id);
    setFilterDraftHolderIds(
      filter.holder_ids == null ? allHolderIds : filter.holder_ids.length === 0 ? [] : filter.holder_ids
    );
    setFilterDraftToolTypes(
      filter.tool_types == null ? [...TOOL_TYPE_VALUES] : filter.tool_types.length === 0 ? [] : filter.tool_types
    );
    setFilterDraftToolNumber(filter.tool_number ?? null);
    setFilterDraftOrder(filter.order ?? 'asc');
  }, [showFilterModal]);

  const filteredTools = useMemo(() => {
    const keys = Object.keys(filterValues) as (keyof UpdateTool)[];
    if (keys.length === 0) return tools;
    return tools.filter((tool) => {
      return keys.every((field) => {
        const filterVal = filterValues[field];
        if (filterVal === undefined) return true;
        const cellVal = getCellValue<unknown>(tool, tool.id, field, edits);
        return valuesEqual(cellVal as string | number | boolean, filterVal);
      });
    });
  }, [tools, filterValues, edits]);

  const toggleFilter = useCallback((field: keyof UpdateTool, value: string | number | boolean) => {
    setFilterValues((prev) => {
      const current = prev[field];
      if (current !== undefined && valuesEqual(current, value)) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const clearFilter = useCallback((field: keyof UpdateTool) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const FILTER_CLICK_DELAY_MS = 250;

  const handleCellClick = useCallback(
    (toolId: string, field: keyof UpdateTool, value: string | number | boolean) => {
      const pending = filterClickCellRef.current;
      if (pending && pending.toolId === toolId && pending.field === field) {
        if (filterClickTimeoutRef.current) {
          clearTimeout(filterClickTimeoutRef.current);
          filterClickTimeoutRef.current = null;
        }
        filterClickCellRef.current = null;
        setEditingCell({ toolId, field });
        return;
      }
      if (filterClickTimeoutRef.current) {
        clearTimeout(filterClickTimeoutRef.current);
      }
      filterClickCellRef.current = { toolId, field };
      filterClickTimeoutRef.current = setTimeout(() => {
        filterClickTimeoutRef.current = null;
        filterClickCellRef.current = null;
        toggleFilter(field, value);
      }, FILTER_CLICK_DELAY_MS);
    },
    [toggleFilter]
  );

  const handleCellDoubleClick = useCallback((toolId: string, field: keyof UpdateTool) => {
    if (filterClickTimeoutRef.current) {
      clearTimeout(filterClickTimeoutRef.current);
      filterClickTimeoutRef.current = null;
    }
    filterClickCellRef.current = null;
    setEditingCell({ toolId, field });
  }, []);

  const setEdit = useCallback((toolId: string, field: keyof UpdateTool, value: string | number | boolean | null) => {
    setEdits((prev) => {
      const next = { ...prev };
      const toolEdits = { ...next[toolId] };
      const originalTool = toolsRef.current.find((t) => t.id === toolId);
      const originalValue = originalTool ? (originalTool as unknown as Record<string, unknown>)[field] : undefined;
      const isUnchanged = value !== null && value !== undefined && value === originalValue;
      if (value === null || value === undefined || isUnchanged) {
        if (field in toolEdits) {
          delete toolEdits[field];
        }
      } else {
        (toolEdits as Record<string, unknown>)[field] = value;
      }
      if (Object.keys(toolEdits).length === 0) {
        delete next[toolId];
      } else {
        next[toolId] = toolEdits;
      }
      editsRef.current = next;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const snapshot = editsRef.current;
    const ids = Object.keys(snapshot);
    if (ids.length === 0) return;

    setSubmitting(true);
    const successIds = new Set<string>();
    let failCount = 0;

    for (const id of ids) {
      const body = snapshot[id];
      if (!body || Object.keys(body).length === 0) continue;

      try {
        await updateTool(id, body);
        successIds.add(id);
      } catch {
        failCount += 1;
      }
    }

    setSubmitting(false);
    if (failCount === 0) {
      setEdits({});
      setEditingCell(null);
      await refetch();
      toast.success(t('toolManagement.saveSuccess'));
    } else {
      toast.error(t('toolManagement.saveError'));
      setEdits((prev) => {
        const next: EditsMap = {};
        for (const id of Object.keys(prev)) {
          if (!successIds.has(id) && prev[id]) next[id] = prev[id];
        }
        return next;
      });
      if (successIds.size > 0) await refetch();
    }
  }, [updateTool, refetch, t]);

  const dirtyCount = Object.keys(edits).length;
  const activeFilterEntries = Object.entries(filterValues) as [keyof UpdateTool, string | number | boolean][];

  const handleAddHolder = useCallback(async () => {
    setAddingHolder(true);
    try {
      await addHolder(addHolderForm);
      setShowAddHolderDialog(false);
      setAddHolderForm({
        holder_name: '',
        diameter_mm_1: 0,
        diameter_mm_2: 0,
        len_mm_1: 0,
        len_mm_2: 0,
      });
      await refetchHolders();
    } catch {
      // toast already in addHolder
    } finally {
      setAddingHolder(false);
    }
  }, [addHolder, addHolderForm, refetchHolders]);

  const handleAddTool = useCallback(async () => {
    if (!addToolForm.holder_id) {
      toast.error(t('toolManagement.addError'));
      return;
    }
    setAddingTool(true);
    try {
      await createTool(addToolForm);
      setShowAddToolDialog(false);
      setAddToolForm(DEFAULT_ADD_TOOL_FORM);
      toast.success(t('toolManagement.addSuccess'));
    } catch {
      toast.error(t('toolManagement.addError'));
    } finally {
      setAddingTool(false);
    }
  }, [addToolForm, createTool, t]);

  const handleOpenToolDetail = useCallback(
    async (toolId: string) => {
      setShowToolDetailDialog(true);
      setDetailTool(null);
      setDetailEdits({});
      setDetailEditingField(null);
      setDetailLoading(true);
      try {
        const tool = await toolsApi.getById(toolId);
        setDetailTool(tool);
      } catch {
        toast.error(t('toolManagement.loadError'));
        setShowToolDetailDialog(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [t]
  );

  const handleDetailSave = useCallback(async () => {
    if (!detailTool || Object.keys(detailEdits).length === 0) return;
    setDetailSaving(true);
    try {
      const updated = await updateTool(detailTool.id, detailEdits);
      setDetailTool(updated);
      setDetailEdits({});
      toast.success(t('toolManagement.saveSuccess'));
    } catch (err) {
      const is409 = (err as { response?: { status?: number } })?.response?.status === 409;
      if (is409 && 'stack_tool_number' in detailEdits) {
        toast.error(t('toolManagement.saveErrorStackToolNumberNotUnique'));
      } else {
        toast.error(t('toolManagement.saveError'));
      }
    } finally {
      setDetailSaving(false);
    }
  }, [detailTool, detailEdits, updateTool, t]);

  const detailVal = useCallback(
    <T,>(field: keyof UpdateTool): T => {
      if (field in detailEdits) return detailEdits[field as keyof UpdateTool] as T;
      return (detailTool as unknown as Record<string, unknown>)?.[field as string] as T;
    },
    [detailEdits, detailTool]
  );

  const setDetailField = useCallback((field: keyof UpdateTool, value: unknown) => {
    setDetailEdits((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDeleteHolder = useCallback(
    async (holderId: string) => {
      if (!window.confirm(t('holders.deleteConfirm'))) return;
      try {
        await deleteHolder(holderId);
      } catch {
        // toast in deleteHolder
      }
    },
    [deleteHolder, t]
  );

  const handleDeleteTool = useCallback(
    async (toolId: string) => {
      if (!window.confirm(t('toolManagement.deleteConfirm'))) return;
      try {
        await deleteTool(toolId);
        toast.success(t('toolManagement.deleteSuccess'));
      } catch {
        toast.error(t('toolManagement.deleteError'));
      }
    },
    [deleteTool, t]
  );

  const startEditingHolderCell = useCallback((holder: HolderModel, field: keyof UpdateHolder) => {
    const raw = (holder as unknown as Record<string, unknown>)[field];
    setHolderEditValue(raw !== undefined && raw !== null ? (typeof raw === 'number' ? raw : String(raw)) : '');
    setEditingHolderCell({ holderId: holder.id, field });
  }, []);

  const handleHolderCellSave = useCallback(
    async (holderId: string, field: keyof UpdateHolder, value: string | number) => {
      setEditingHolderCell(null);
      setHolderEditValue(null);
      const holder = holders.find((h) => h.id === holderId);
      if (!holder) return;
      const current = (holder as unknown as Record<string, unknown>)[field];
      if (current === value) return;
      try {
        await updateHolder(holderId, { [field]: value });
      } catch {
        // toast handled by updateHolder or API
      }
    },
    [holders, updateHolder]
  );

  /** On holder select in tools table: send update immediately and close dropdown. */
  const handleToolHolderSelect = useCallback(
    async (toolId: string, holderId: string | null) => {
      setHolderUpdatingToolId(toolId);
      try {
        await updateTool(toolId, { holder_id: holderId });
        setEdits((prev) => {
          const next = { ...prev };
          const toolEdits = { ...next[toolId] };
          delete toolEdits.holder_id;
          if (Object.keys(toolEdits).length === 0) delete next[toolId];
          else next[toolId] = toolEdits;
          return next;
        });
        toast.success(t('toolManagement.saveSuccess'));
      } catch {
        toast.error(t('toolManagement.saveError'));
      } finally {
        setHolderUpdatingToolId(null);
      }
    },
    [updateTool, t]
  );

  /** On tool_type select in tools table: send update immediately and close dropdown. */
  const handleToolTypeSelect = useCallback(
    async (toolId: string, toolType: ToolType) => {
      setToolTypeUpdatingToolId(toolId);
      try {
        await updateTool(toolId, { tool_type: toolType });
        setEdits((prev) => {
          const next = { ...prev };
          const toolEdits = { ...next[toolId] };
          delete toolEdits.tool_type;
          if (Object.keys(toolEdits).length === 0) delete next[toolId];
          else next[toolId] = toolEdits;
          return next;
        });
        toast.success(t('toolManagement.saveSuccess'));
      } catch {
        toast.error(t('toolManagement.saveError'));
      } finally {
        setToolTypeUpdatingToolId(null);
      }
    },
    [updateTool, t]
  );

  const filterLabel = useCallback(
    (_field: keyof UpdateTool, value: string | number | boolean) => {
      if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
      return String(value);
    },
    [t]
  );

  const handleResizeMouseDown = useCallback((col: string, e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidthsRef.current[col] ?? DEFAULT_COL_WIDTHS[col] ?? 80;
    resizingRef.current = { col, startX, startWidth };

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const delta = ev.clientX - resizingRef.current.startX;
      const newWidth = Math.max(48, resizingRef.current.startWidth + delta);
      setColWidths((prev) => ({ ...prev, [resizingRef.current!.col]: newWidth }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setColWidths((prev) => {
        try {
          localStorage.setItem(LS_COL_WIDTHS_KEY, JSON.stringify(prev));
        } catch {
          // ignore
        }
        return prev;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleColDragStart = useCallback((key: keyof UpdateTool, e: ReactDragEvent) => {
    dragStartKeyRef.current = key;
    setDragSourceKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key); // required for Firefox
  }, []);

  const handleColDragOver = useCallback((key: keyof UpdateTool, e: ReactDragEvent) => {
    if (!dragStartKeyRef.current || dragStartKeyRef.current === key) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(key);
  }, []);

  const handleColDragLeave = useCallback((e: ReactDragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDragOverKey(null);
    }
  }, []);

  const handleColDrop = useCallback((targetKey: keyof UpdateTool, e: ReactDragEvent) => {
    e.preventDefault();
    const sourceKey = dragStartKeyRef.current;
    if (!sourceKey || sourceKey === targetKey) {
      setDragOverKey(null);
      return;
    }
    setColOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(sourceKey);
      const toIdx = next.indexOf(targetKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, sourceKey);
      try {
        localStorage.setItem(LS_COL_ORDER_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
    setDragOverKey(null);
    setDragSourceKey(null);
    dragStartKeyRef.current = null;
  }, []);

  const handleColDragEnd = useCallback(() => {
    setDragOverKey(null);
    setDragSourceKey(null);
    dragStartKeyRef.current = null;
  }, []);

  return (
    <div className="max-w-[calc(80rem+4cm)] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <style>{`
        /* Checkbox „Vybrat vše“ v hlavičce panelu: tmavě šedá místo modré */
        .multiselect-filter-panel .p-multiselect-select-all .p-checkbox[data-p-highlight="true"] .p-checkbox-box {
          background: rgb(75 85 99) !important;
          border-color: rgb(75 85 99) !important;
        }
        .multiselect-filter-panel .p-multiselect-select-all .p-checkbox[data-p-highlight="true"] .p-checkbox-box .p-checkbox-icon,
        .multiselect-filter-panel .p-multiselect-select-all .p-checkbox[data-p-highlight="true"] .p-checkbox-box svg {
          color: white !important;
          fill: white !important;
        }
        /* Checkbox u položek v multiselectu: méně výrazná – tmavě šedá s bílou fajfkou */
        .multiselect-filter-panel .p-multiselect-item.p-highlight .p-checkbox .p-checkbox-box {
          background: rgb(75 85 99) !important;
          border-color: rgb(75 85 99) !important;
        }
        .multiselect-filter-panel .p-multiselect-item.p-highlight .p-checkbox .p-checkbox-box .p-checkbox-icon,
        .multiselect-filter-panel .p-multiselect-item.p-highlight .p-checkbox .p-checkbox-box svg {
          color: white !important;
          fill: white !important;
        }
        .dark .multiselect-filter-panel .p-multiselect-item.p-highlight .p-checkbox .p-checkbox-box {
          background: rgb(75 85 99) !important;
          border-color: rgb(75 85 99) !important;
        }
        .dark .multiselect-filter-panel .p-multiselect-item.p-highlight .p-checkbox .p-checkbox-box .p-checkbox-icon,
        .dark .multiselect-filter-panel .p-multiselect-item.p-highlight .p-checkbox .p-checkbox-box svg {
          color: white !important;
          fill: white !important;
        }
        /* Detail dialog: read-only field value — shows edit cursor on hover */
        .detail-val {
          min-height: 2rem;
          display: flex;
          align-items: center;
          padding: 0 0.25rem;
          font-size: 0.875rem;
          color: rgb(31 41 55);
          border: 1px solid transparent;
          border-radius: 0.375rem;
          cursor: default;
          user-select: none;
          transition: border-color 0.15s;
        }
        .detail-val:hover {
          border-color: rgb(209 213 219);
          cursor: text;
        }
        .dark .detail-val {
          color: rgb(229 231 235);
        }
        .dark .detail-val:hover {
          border-color: rgb(55 65 81);
        }
      `}</style>
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              icon={<ArrowLeft size={18} />}
              onClick={() => navigate('/')}
              className="p-button-text"
              label={t('toolManagement.backToProjects')}
              aria-label={t('toolManagement.backToProjects')}
            />
            <h1 className="text-3xl font-bold text-gray-900">{t('toolManagement.title')}</h1>
          </div>
          {(dirtyCount > 0 || editingCell !== null) && (
            <Button
              label={t('toolManagement.submit')}
              loading={submitting}
              disabled={submitting}
              onClick={handleSubmit}
            />
          )}
        </div>

        {error && <ErrorMessage message={error} />}

        {isLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 p-8 text-center text-gray-500">
            {t('common.loading')}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                label={`${t('toolManagement.predictToolsLine1')}\n${t('toolManagement.predictToolsLine2')}`}
                onClick={() => setShowPredictDialog(true)}
                className="p-button-sm p-button-outlined"
                aria-label={t('toolManagement.predictToolsAriaLabel')}
                pt={{ label: { className: 'whitespace-pre-line text-center leading-tight' } }}
              />
              <div className="flex flex-wrap items-center gap-2">
                {(listFilter.holder_ids != null ||
                  listFilter.tool_types != null ||
                  listFilter.tool_number != null ||
                  listFilter.order !== 'asc') && (
                  <span className="text-sm text-gray-500">
                    {[
                      listFilter.holder_ids != null && listFilter.holder_ids.length > 0
                        ? `${listFilter.holder_ids.length} ${t('toolManagement.filterByHolder')}`
                        : null,
                      listFilter.tool_types != null && listFilter.tool_types.length > 0
                        ? `${listFilter.tool_types.length} ${t('toolManagement.filterByType')}`
                        : null,
                      listFilter.tool_number != null
                        ? `${t('toolManagement.filterByToolNumber')} ${listFilter.tool_number}`
                        : null,
                      listFilter.order === 'desc' ? t('toolManagement.sortDesc') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
                {/* TODO: re-enable filter button when needed */}
                {true && (
                  <Button
                    icon={<Filter size={18} />}
                    label={t('toolManagement.openFilter')}
                    onClick={() => setShowFilterModal(true)}
                    className="p-button-outlined p-button-sm"
                    aria-label={t('toolManagement.filterDialogTitle')}
                  />
                )}
                <Button
                  icon={<Plus size={18} />}
                  label={t('toolManagement.add')}
                  onClick={() => setShowAddToolDialog(true)}
                  className="p-button-sm"
                  aria-label={t('toolManagement.addDialogTitle')}
                />
              </div>
            </div>
            {tools.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 p-8 text-center text-gray-500">
                {t('toolManagement.noToolsMessage')}
              </div>
            ) : (
              <>
                {activeFilterEntries.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">{t('toolManagement.activeFilters')}</span>
                    {activeFilterEntries.map(([field, value]) => (
                      <span
                        key={field}
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm"
                      >
                        <span className="font-medium">{filterLabel(field, value)}</span>
                        <button
                          type="button"
                          onClick={() => clearFilter(field)}
                          className="rounded-full p-0.5 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary"
                          aria-label={t('common.remove')}
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="rounded-xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-fixed" role="table" style={{ minWidth: '48rem' }}>
                      <colgroup>
                        {orderedColumns.map((col) => (
                          <col key={col.key} style={{ width: colWidths[col.key] ?? DEFAULT_COL_WIDTHS[col.key] }} />
                        ))}
                        <col style={{ width: colWidths._actions ?? DEFAULT_COL_WIDTHS._actions }} />
                      </colgroup>
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50/90">
                          {orderedColumns.map((col) => (
                            <th
                              key={col.key}
                              draggable={true}
                              onDragStart={(e) => handleColDragStart(col.key, e)}
                              onDragOver={(e) => handleColDragOver(col.key, e)}
                              onDragLeave={handleColDragLeave}
                              onDrop={(e) => handleColDrop(col.key, e)}
                              onDragEnd={handleColDragEnd}
                              className={`text-left font-semibold text-gray-700 px-4 py-3 first:pl-5 relative select-none overflow-hidden cursor-grab active:cursor-grabbing transition-colors ${
                                dragSourceKey === col.key ? 'opacity-40' : ''
                              } ${dragOverKey === col.key ? 'bg-primary/10' : ''}`}
                              role="columnheader"
                              title={col.key === 'holder_id' ? t('toolManagement.holderClickToSelect') : undefined}
                            >
                              {dragOverKey === col.key && (
                                <span className="absolute left-0 top-0 h-full w-0.5 bg-primary z-20" />
                              )}
                              <span className="inline-flex items-center gap-1 w-full min-w-0">
                                <GripVertical
                                  size={12}
                                  className="shrink-0 text-gray-300 group-hover:text-gray-400"
                                  aria-hidden
                                />
                                <span className="min-w-0">
                                  {col.labelKey2 ? (
                                    <>
                                      <span className="block truncate">{t(col.labelKey)}</span>
                                      <span className="block text-gray-500 font-normal text-xs truncate">
                                        {t(col.labelKey2)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="block truncate">{t(col.labelKey)}</span>
                                  )}
                                </span>
                              </span>
                              <div
                                className="absolute right-0 top-0 h-full w-2 cursor-col-resize z-10 group/rh flex items-center justify-center hover:bg-primary/20 active:bg-primary/40"
                                onMouseDown={(e) => handleResizeMouseDown(col.key, e)}
                                onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              >
                                <span className="block w-px h-4 rounded-full bg-gray-300 group-hover/rh:bg-primary/60 group-active/rh:bg-primary transition-colors" />
                              </div>
                            </th>
                          ))}
                          <th
                            className="text-right font-semibold text-gray-700 px-4 py-3 relative select-none overflow-hidden"
                            role="columnheader"
                          >
                            {t('common.actions')}
                            <div
                              className="absolute right-0 top-0 h-full w-2 cursor-col-resize z-10 group/rh flex items-center justify-center hover:bg-primary/20 active:bg-primary/40"
                              onMouseDown={(e) => handleResizeMouseDown('_actions', e)}
                            >
                              <span className="block w-px h-4 rounded-full bg-gray-300 group-hover/rh:bg-primary/60 group-active/rh:bg-primary transition-colors" />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTools.length === 0 ? (
                          <tr>
                            <td
                              colSpan={orderedColumns.length + 1}
                              className="px-4 py-8 text-center text-gray-500"
                              role="cell"
                            >
                              {tools.length === 0
                                ? t('toolManagement.noToolsMessage')
                                : t('toolManagement.noMatchFilter')}
                            </td>
                          </tr>
                        ) : (
                          filteredTools.map((tool, rowIndex) => (
                            <tr
                              key={tool.id}
                              role="row"
                              className={`border-b border-gray-100 transition-colors ${
                                rowIndex % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'
                              } hover:bg-gray-50/80`}
                            >
                              {orderedColumns.map((col) => {
                                const field = col.key;
                                const isEditing = editingCell?.toolId === tool.id && editingCell?.field === field;
                                const value = getCellValue<unknown>(tool, tool.id, field, edits);
                                const displayVal = value ?? '';

                                if (field === 'holder_id') {
                                  const displayName = getHolderDisplayName(tool, tool.id, edits, holders);
                                  const holderIdValue =
                                    (getCellValue<string>(tool, tool.id, 'holder_id', edits) as string) ??
                                    tool.holder_id ??
                                    null;
                                  const isHolderUpdating = holderUpdatingToolId === tool.id;
                                  const isFilterActive =
                                    filterValues[field] !== undefined &&
                                    valuesEqual(
                                      filterValues[field]!,
                                      (holderIdValue ?? '') as string | number | boolean
                                    );
                                  return (
                                    <td
                                      key={field}
                                      className="px-4 py-2 align-middle"
                                      role="cell"
                                      onClick={(e) => e.stopPropagation()}
                                      onDoubleClick={(e) => e.stopPropagation()}
                                    >
                                      {isHolderUpdating ? (
                                        <span className="flex items-center gap-1.5 text-sm text-gray-500 px-2 py-1">
                                          <Loader2 size={14} className="shrink-0 animate-spin" aria-hidden />
                                          {displayName}
                                        </span>
                                      ) : (
                                        <Dropdown
                                          value={holderIdValue}
                                          options={[
                                            { label: '—', value: null },
                                            ...holders.map((h) => ({ label: h.holder_name || h.id, value: h.id })),
                                          ]}
                                          onChange={(e) => handleToolHolderSelect(tool.id, e.value)}
                                          placeholder={t('holders.holderName')}
                                          unstyled
                                          pt={{
                                            root: {
                                              className: `flex items-center w-[calc(100%+24px)] cursor-pointer rounded px-2 py-1 -mx-2 -my-1 transition-colors ${
                                                isFilterActive
                                                  ? 'bg-primary/10 text-primary font-medium hover:bg-primary/20'
                                                  : 'text-gray-700 hover:bg-gray-100'
                                              }`,
                                              style: {
                                                border: 'none',
                                                boxShadow: 'none',
                                                outline: 'none',
                                                background: isFilterActive ? undefined : 'transparent',
                                              },
                                            },
                                            input: {
                                              className: 'flex-1 text-sm text-left truncate',
                                              style: { padding: 0 },
                                            },
                                            trigger: {
                                              className: 'shrink-0 ml-1 flex items-center text-gray-400 !w-auto',
                                            },
                                            panel: {
                                              className:
                                                'bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 mt-1',
                                            },
                                            wrapper: { className: 'max-h-48 overflow-auto' },
                                            list: { className: '' },
                                            item: {
                                              className:
                                                'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 text-gray-700 rounded-none [&.p-highlight]:!bg-[oklch(93.2%_0.032_255.585)] [&.p-highlight]:!text-gray-800 [&.p-highlight]:dark:!bg-blue-400 [&.p-highlight]:dark:!text-gray-200',
                                            },
                                          }}
                                          dropdownIcon={<ChevronRight size={14} className="rotate-90" aria-hidden />}
                                        />
                                      )}
                                    </td>
                                  );
                                }

                                if (field === 'tool_type') {
                                  const currentType =
                                    (getCellValue<string>(tool, tool.id, 'tool_type', edits) as string) ??
                                    tool.tool_type ??
                                    null;
                                  const isToolTypeUpdating = toolTypeUpdatingToolId === tool.id;
                                  const isFilterActive =
                                    filterValues[field] !== undefined &&
                                    valuesEqual(filterValues[field]!, (currentType ?? '') as string | number | boolean);
                                  return (
                                    <td
                                      key={field}
                                      className="px-4 py-2 align-middle"
                                      role="cell"
                                      onClick={(e) => e.stopPropagation()}
                                      onDoubleClick={(e) => e.stopPropagation()}
                                    >
                                      {isToolTypeUpdating ? (
                                        <span className="flex items-center gap-1.5 text-sm text-gray-500 px-2 py-1">
                                          <Loader2 size={14} className="shrink-0 animate-spin" aria-hidden />
                                          {currentType}
                                        </span>
                                      ) : (
                                        <Dropdown
                                          value={currentType}
                                          options={TOOL_TYPE_VALUES.map((tt) => ({ label: tt, value: tt }))}
                                          onChange={(e) => handleToolTypeSelect(tool.id, e.value)}
                                          placeholder="—"
                                          unstyled
                                          pt={{
                                            root: {
                                              className: `flex items-center w-[calc(100%+24px)] cursor-pointer rounded px-2 py-1 -mx-2 -my-1 transition-colors ${
                                                isFilterActive
                                                  ? 'bg-primary/10 text-primary font-medium hover:bg-primary/20'
                                                  : 'text-gray-700 hover:bg-gray-100'
                                              }`,
                                              style: {
                                                border: 'none',
                                                boxShadow: 'none',
                                                outline: 'none',
                                                background: isFilterActive ? undefined : 'transparent',
                                              },
                                            },
                                            input: {
                                              className: 'flex-1 text-sm text-left truncate',
                                              style: { padding: 0 },
                                            },
                                            trigger: {
                                              className: 'shrink-0 ml-1 flex items-center text-gray-400 !w-auto',
                                            },
                                            panel: {
                                              className:
                                                'bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 mt-1',
                                            },
                                            wrapper: { className: 'max-h-48 overflow-auto' },
                                            list: { className: '' },
                                            item: {
                                              className:
                                                'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 text-gray-700 rounded-none [&.p-highlight]:!bg-[oklch(93.2%_0.032_255.585)] [&.p-highlight]:!text-gray-800 [&.p-highlight]:dark:!bg-blue-400 [&.p-highlight]:dark:!text-gray-200',
                                            },
                                          }}
                                          dropdownIcon={<ChevronRight size={14} className="rotate-90" aria-hidden />}
                                        />
                                      )}
                                    </td>
                                  );
                                }

                                if (field === 'support_finish') {
                                  const boolVal = Boolean(displayVal);
                                  return (
                                    <td key={field} className="px-4 py-2 align-middle text-center" role="cell">
                                      <div className="flex justify-center">
                                        <Checkbox
                                          checked={boolVal}
                                          onChange={(e) => setEdit(tool.id, 'support_finish', e.checked ?? false)}
                                        />
                                      </div>
                                    </td>
                                  );
                                }

                                if (isEditing) {
                                  if (field === 'tool_name' || field === 'tool_comment' || field === 'tool_rotation') {
                                    return (
                                      <td key={field} className="px-4 py-1 align-middle" role="cell">
                                        <InputText
                                          value={String(displayVal)}
                                          onChange={(e) => setEdit(tool.id, field, e.target.value)}
                                          onBlur={() => setEditingCell(null)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              e.preventDefault();
                                              setEditingCell(null);
                                            }
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              void handleSubmit();
                                            }
                                          }}
                                          className="w-full p-inputtext-sm"
                                          autoFocus
                                        />
                                      </td>
                                    );
                                  }
                                  if (field === 'tool_number') {
                                    return (
                                      <td key={field} className="px-4 py-1 align-middle" role="cell">
                                        <InputText
                                          value={String(displayVal)}
                                          onChange={(e) =>
                                            setEdit(tool.id, field, keepOnlyAllowedToolNumberChars(e.target.value))
                                          }
                                          onBlur={() => setEditingCell(null)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              e.preventDefault();
                                              setEditingCell(null);
                                            }
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              void handleSubmit();
                                            }
                                            blockInvalidToolNumberKeys(e);
                                          }}
                                          className="w-full p-inputtext-sm"
                                          autoFocus
                                        />
                                      </td>
                                    );
                                  }
                                  if (
                                    field === 'tool_diameter' ||
                                    field === 'tool_overall_len' ||
                                    field === 'tool_len' ||
                                    field === 'spindle' ||
                                    field === 'feed_mach_value'
                                  ) {
                                    return (
                                      <td key={field} className="px-4 py-1 align-middle" role="cell">
                                        <InputNumber
                                          value={Number(displayVal)}
                                          onValueChange={(e) => {
                                            setEdit(tool.id, field, e.value ?? 0);
                                          }}
                                          onBlur={() => setEditingCell(null)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              e.preventDefault();
                                              setEditingCell(null);
                                            }
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              // Blur triggers PrimeReact's internal commit (onValueChange),
                                              // which synchronously updates editsRef before handleSubmit reads it.
                                              (e.target as HTMLElement).blur();
                                              void handleSubmit();
                                            }
                                            blockInvalidNumberKeys(e);
                                          }}
                                          className="w-full p-inputnumber-sm"
                                          mode="decimal"
                                          minFractionDigits={0}
                                          maxFractionDigits={field === 'feed_mach_value' ? 2 : 4}
                                          autoFocus
                                        />
                                      </td>
                                    );
                                  }
                                }

                                const isFilterActive =
                                  filterValues[field] !== undefined &&
                                  valuesEqual(filterValues[field]!, displayVal as string | number | boolean);
                                return (
                                  <td key={field} className="px-4 py-2 align-middle overflow-hidden" role="cell">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleCellClick(tool.id, field, displayVal as string | number | boolean)
                                      }
                                      onDoubleClick={() => handleCellDoubleClick(tool.id, field)}
                                      className={`w-full text-left rounded px-2 py-1 -mx-2 -my-1 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset truncate block ${
                                        isFilterActive
                                          ? 'bg-primary/10 text-primary font-medium'
                                          : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                      title={
                                        typeof displayVal === 'boolean'
                                          ? displayVal
                                            ? t('common.yes')
                                            : t('common.no')
                                          : String(displayVal)
                                      }
                                    >
                                      {typeof displayVal === 'boolean'
                                        ? displayVal
                                          ? t('common.yes')
                                          : t('common.no')
                                        : String(displayVal)}
                                    </button>
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-right whitespace-nowrap">
                                <Button
                                  icon={<Eye size={16} />}
                                  onClick={() => handleOpenToolDetail(tool.id)}
                                  className="p-button-text p-button-sm"
                                  aria-label={t('toolManagement.viewTool')}
                                />
                                <Button
                                  icon={<Trash2 size={16} />}
                                  onClick={() => handleDeleteTool(tool.id)}
                                  className="p-button-text p-button-danger p-button-sm"
                                  aria-label={t('common.delete')}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/60 px-4 py-2">
                    <div className="flex items-center justify-center gap-2 flex-1">
                      <Button
                        icon={<ChevronLeft size={18} />}
                        className="p-button-text p-button-sm"
                        disabled={!hasPrevPage}
                        onClick={() => setPage(Math.max(1, page - 1))}
                        aria-label={t('common.previousPage')}
                      />
                      <span className="text-sm text-gray-600">
                        {totalPages != null
                          ? `${t('common.page')} ${page} ${t('common.of')} ${totalPages}`
                          : `${t('common.page')} ${page}`}
                      </span>
                      <Button
                        icon={<ChevronRight size={18} />}
                        className="p-button-text p-button-sm"
                        disabled={!hasNextPage}
                        onClick={() => setPage(page + 1)}
                        aria-label={t('common.nextPage')}
                      />
                    </div>
                    {totalCount != null && (
                      <span className="text-sm text-gray-600 shrink-0">
                        {activeFilterEntries.length > 0
                          ? `${filteredTools.length} / ${tools.length}`
                          : `${Math.min(page * pageSize, totalCount)} / ${totalCount}`}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Holders (Držáky) section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">{t('holders.title')}</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                id="holder-dimensions-info-btn"
                onClick={() => setShowDimensionsModal(true)}
                className="flex items-center justify-center w-8 h-8 rounded-full text-blue-600 hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
                aria-label={t('holders.showDimensionsDiagram')}
              >
                <Info size={18} strokeWidth={2.5} />
              </button>
              <Tooltip target="#holder-dimensions-info-btn" position="bottom">
                {t('holders.showDimensionsDiagram')}
              </Tooltip>
              <Button
                icon={<Plus size={18} />}
                label={t('holders.add')}
                onClick={() => setShowAddHolderDialog(true)}
                className="p-button-sm"
              />
            </div>
          </div>
          {holdersLoading ? (
            <div className="bg-white rounded-xl border border-gray-200/80 p-6 text-center text-gray-500">
              {t('common.loading')}
            </div>
          ) : holders.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200/80 p-6 text-center text-gray-500">
              {t('holders.noHoldersMessage')}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table" style={{ minWidth: '32rem' }}>
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/90">
                      <th className="text-left font-semibold text-gray-700 px-4 py-3">{t('holders.holderName')}</th>
                      <th className="text-left font-semibold text-gray-700 px-4 py-3">{t('holders.diameterMm1')}</th>
                      <th className="text-left font-semibold text-gray-700 px-4 py-3">{t('holders.diameterMm2')}</th>
                      <th className="text-left font-semibold text-gray-700 px-4 py-3">{t('holders.lenMm1')}</th>
                      <th className="text-left font-semibold text-gray-700 px-4 py-3">{t('holders.lenMm2')}</th>
                      <th className="text-right font-semibold text-gray-700 px-4 py-3 w-24">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holders.map((holder, idx) => (
                      <tr
                        key={holder.id}
                        className={`border-b border-gray-100 ${
                          idx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'
                        } hover:bg-gray-50/80`}
                      >
                        {/* holder_name */}
                        <td className="px-4 py-1 align-middle" role="cell">
                          {editingHolderCell?.holderId === holder.id && editingHolderCell?.field === 'holder_name' ? (
                            <InputText
                              value={typeof holderEditValue === 'string' ? holderEditValue : ''}
                              onChange={(e) => setHolderEditValue(e.target.value)}
                              onBlur={() => {
                                if (holderEditValue !== null)
                                  handleHolderCellSave(holder.id, 'holder_name', holderEditValue);
                                else setEditingHolderCell(null);
                                setHolderEditValue(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }
                                if (e.key === 'Enter') {
                                  const v = (e.target as HTMLInputElement).value;
                                  handleHolderCellSave(holder.id, 'holder_name', v);
                                }
                              }}
                              className="w-full p-inputtext-sm"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              onDoubleClick={() => startEditingHolderCell(holder, 'holder_name')}
                              className="w-full text-left rounded px-2 py-1 -mx-2 -my-1 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                              title={t('holders.doubleClickToEdit')}
                            >
                              {holder.holder_name || '-'}
                            </button>
                          )}
                        </td>
                        {/* diameter_mm_1 */}
                        <td className="px-4 py-1 align-middle" role="cell">
                          {editingHolderCell?.holderId === holder.id && editingHolderCell?.field === 'diameter_mm_1' ? (
                            <InputNumber
                              value={typeof holderEditValue === 'number' ? holderEditValue : holder.diameter_mm_1}
                              onValueChange={(e) => setHolderEditValue(e.value ?? 0)}
                              onBlur={() => {
                                if (holderEditValue !== null)
                                  handleHolderCellSave(holder.id, 'diameter_mm_1', holderEditValue);
                                setEditingHolderCell(null);
                                setHolderEditValue(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }
                              }}
                              className="w-full p-inputnumber-sm"
                              mode="decimal"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              onDoubleClick={() => startEditingHolderCell(holder, 'diameter_mm_1')}
                              className="w-full text-left rounded px-2 py-1 -mx-2 -my-1 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                              title={t('holders.doubleClickToEdit')}
                            >
                              {holder.diameter_mm_1}
                            </button>
                          )}
                        </td>
                        {/* diameter_mm_2 */}
                        <td className="px-4 py-1 align-middle" role="cell">
                          {editingHolderCell?.holderId === holder.id && editingHolderCell?.field === 'diameter_mm_2' ? (
                            <InputNumber
                              value={typeof holderEditValue === 'number' ? holderEditValue : holder.diameter_mm_2}
                              onValueChange={(e) => setHolderEditValue(e.value ?? 0)}
                              onBlur={() => {
                                if (holderEditValue !== null)
                                  handleHolderCellSave(holder.id, 'diameter_mm_2', holderEditValue);
                                setEditingHolderCell(null);
                                setHolderEditValue(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }
                              }}
                              className="w-full p-inputnumber-sm"
                              mode="decimal"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              onDoubleClick={() => startEditingHolderCell(holder, 'diameter_mm_2')}
                              className="w-full text-left rounded px-2 py-1 -mx-2 -my-1 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                              title={t('holders.doubleClickToEdit')}
                            >
                              {holder.diameter_mm_2}
                            </button>
                          )}
                        </td>
                        {/* len_mm_1 */}
                        <td className="px-4 py-1 align-middle" role="cell">
                          {editingHolderCell?.holderId === holder.id && editingHolderCell?.field === 'len_mm_1' ? (
                            <InputNumber
                              value={typeof holderEditValue === 'number' ? holderEditValue : holder.len_mm_1}
                              onValueChange={(e) => setHolderEditValue(e.value ?? 0)}
                              onBlur={() => {
                                if (holderEditValue !== null)
                                  handleHolderCellSave(holder.id, 'len_mm_1', holderEditValue);
                                setEditingHolderCell(null);
                                setHolderEditValue(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }
                              }}
                              className="w-full p-inputnumber-sm"
                              mode="decimal"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              onDoubleClick={() => startEditingHolderCell(holder, 'len_mm_1')}
                              className="w-full text-left rounded px-2 py-1 -mx-2 -my-1 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                              title={t('holders.doubleClickToEdit')}
                            >
                              {holder.len_mm_1}
                            </button>
                          )}
                        </td>
                        {/* len_mm_2 */}
                        <td className="px-4 py-1 align-middle" role="cell">
                          {editingHolderCell?.holderId === holder.id && editingHolderCell?.field === 'len_mm_2' ? (
                            <InputNumber
                              value={typeof holderEditValue === 'number' ? holderEditValue : holder.len_mm_2}
                              onValueChange={(e) => setHolderEditValue(e.value ?? 0)}
                              onBlur={() => {
                                if (holderEditValue !== null)
                                  handleHolderCellSave(holder.id, 'len_mm_2', holderEditValue);
                                setEditingHolderCell(null);
                                setHolderEditValue(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }
                              }}
                              className="w-full p-inputnumber-sm"
                              mode="decimal"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              onDoubleClick={() => startEditingHolderCell(holder, 'len_mm_2')}
                              className="w-full text-left rounded px-2 py-1 -mx-2 -my-1 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                              title={t('holders.doubleClickToEdit')}
                            >
                              {holder.len_mm_2}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            icon={<Trash2 size={16} />}
                            onClick={() => handleDeleteHolder(holder.id)}
                            className="p-button-text p-button-danger p-button-sm"
                            aria-label={t('common.delete')}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <Dialog
          header={t('toolManagement.filterDialogTitle')}
          visible={showFilterModal}
          onHide={() => setShowFilterModal(false)}
          className="w-full max-w-md"
          pt={TOOL_MANAGEMENT_DIALOG_PT}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                id="filter-reset-btn"
                label={t('toolManagement.filterReset')}
                className="p-button-text"
                onClick={() => {
                  setFilterDraftHolderIds(holders.map((h) => h.id));
                  setFilterDraftToolTypes([...TOOL_TYPE_VALUES]);
                  setFilterDraftToolNumber(null);
                  setFilterDraftOrder('asc');
                }}
              />
              <Button
                label={t('toolManagement.filterApply')}
                onClick={() => {
                  const allHolderIds = holders.map((h) => h.id);
                  const holderIds =
                    filterDraftHolderIds.length === allHolderIds.length
                      ? null
                      : filterDraftHolderIds.length === 0
                        ? []
                        : filterDraftHolderIds;
                  const toolTypes =
                    filterDraftToolTypes.length === TOOL_TYPE_VALUES.length
                      ? null
                      : filterDraftToolTypes.length === 0
                        ? []
                        : filterDraftToolTypes;
                  setListFilter({
                    holder_ids: holderIds,
                    tool_types: toolTypes,
                    tool_number: filterDraftToolNumber ?? null,
                    order: filterDraftOrder,
                  });
                  setShowFilterModal(false);
                }}
              />
              <Button label={t('common.close')} className="p-button-text" onClick={() => setShowFilterModal(false)} />
            </div>
          }
        >
          <Tooltip target="#filter-reset-btn">{t('toolManagement.filterResetTooltip')}</Tooltip>
          <div className="flex flex-col gap-5 pt-1">
            {/* Držák */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('toolManagement.filterByHolder')}
              </label>
              <MultiSelect
                value={filterDraftHolderIds}
                options={holders.map((h) => ({ label: h.holder_name || h.id, value: h.id }))}
                onChange={(e) => setFilterDraftHolderIds(e.value ?? [])}
                onSelectAll={(e) =>
                  // e.checked = current state BEFORE click (PrimeReact v10 passes props.selectAll, not the new value)
                  setFilterDraftHolderIds(e.checked ? [] : holders.map((h) => h.id))
                }
                selectAll={holders.length > 0 && filterDraftHolderIds.length === holders.length}
                showSelectAll
                selectAllLabel={t('toolManagement.filterAll')}
                placeholder={t('toolManagement.filterAll')}
                maxSelectedLabels={3}
                selectedItemsLabel={`{0} ${t('toolManagement.filterByHolder').toLowerCase()}`}
                className="w-full"
                display="chip"
                optionLabel="label"
                optionValue="value"
                unstyled
                pt={{
                  root: {
                    className:
                      'flex items-center w-full cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 min-h-[38px] transition-colors hover:border-gray-300 dark:hover:border-gray-600',
                  },
                  labelContainer: { className: 'flex-1 min-w-0 flex flex-wrap items-center gap-1' },
                  label: { className: 'flex flex-wrap items-center gap-1 min-w-0' },
                  trigger: {
                    className: 'ml-auto shrink-0 flex items-center pl-2 text-gray-400 dark:text-gray-500',
                  },
                  token: {
                    className:
                      'rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 py-0.5 px-2 my-0.5 text-xs font-medium inline-flex items-center gap-1',
                  },
                  tokenLabel: { className: 'text-blue-700 dark:text-blue-300' },
                  panel: {
                    className:
                      'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 mt-1 overflow-hidden',
                  },
                  header: {
                    className: 'flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-800',
                  },
                  headerCheckbox: {
                    root: { className: 'flex items-center shrink-0' },
                    box: {
                      className:
                        'w-4 h-4 border-2 border-gray-400 dark:border-gray-500 rounded bg-transparent flex items-center justify-center transition-colors [&.p-highlight]:border-gray-600 dark:[&.p-highlight]:border-gray-300',
                    },
                    icon: { className: 'text-gray-700 dark:text-gray-200 w-2.5 h-2.5' },
                  },
                  wrapper: { className: 'max-h-52 overflow-auto' },
                  list: { className: 'py-1 list-none m-0' },
                  item: {
                    className:
                      'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 transition-colors',
                  },
                  checkbox: {
                    root: { className: 'flex items-center shrink-0 mr-2' },
                    box: {
                      className:
                        'w-4 h-4 border-2 border-gray-400 dark:border-gray-500 rounded bg-transparent flex items-center justify-center transition-colors [&.p-highlight]:border-gray-600 dark:[&.p-highlight]:border-gray-300',
                    },
                    icon: { className: 'text-gray-700 dark:text-gray-200 w-2.5 h-2.5' },
                  },
                }}
              />
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* Typ nástroje – checkboxy */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('toolManagement.filterByType')}
                </label>
                <button
                  type="button"
                  onClick={() =>
                    filterDraftToolTypes.length === TOOL_TYPE_VALUES.length
                      ? setFilterDraftToolTypes([])
                      : setFilterDraftToolTypes([...TOOL_TYPE_VALUES])
                  }
                  className="text-xs font-medium text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  {filterDraftToolTypes.length === TOOL_TYPE_VALUES.length
                    ? t('toolManagement.filterDeselectAll')
                    : t('toolManagement.filterSelectAll')}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-0.5">
                {TOOL_TYPE_VALUES.map((tt) => (
                  <div
                    key={tt}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Checkbox
                      inputId={`filter-tt-${tt}`}
                      checked={filterDraftToolTypes.includes(tt)}
                      onChange={(e) =>
                        setFilterDraftToolTypes((prev) => (e.checked ? [...prev, tt] : prev.filter((v) => v !== tt)))
                      }
                      unstyled
                      pt={{
                        root: { className: 'relative inline-flex cursor-pointer select-none shrink-0 w-4 h-4' },
                        input: {
                          className:
                            'absolute appearance-none top-0 left-0 size-full p-0 m-0 opacity-0 z-10 outline-none cursor-pointer',
                        },
                        box: {
                          className:
                            'w-4 h-4 border-2 border-gray-400 dark:border-gray-500 rounded bg-transparent flex items-center justify-center transition-colors data-[p-highlight=true]:bg-blue-500 data-[p-highlight=true]:border-blue-500 dark:data-[p-highlight=true]:bg-blue-400 dark:data-[p-highlight=true]:border-blue-400',
                        },
                        icon: { className: 'text-white w-2.5 h-2.5' },
                      }}
                    />
                    <label
                      htmlFor={`filter-tt-${tt}`}
                      className="text-sm text-gray-700 dark:text-gray-200 leading-none cursor-pointer select-none"
                    >
                      {tt}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* Číslo nástroje */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="filter-tool-number"
                  className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  {t('toolManagement.filterByToolNumber')}
                </label>
                {filterDraftToolNumber != null && (
                  <button
                    type="button"
                    onClick={() => setFilterDraftToolNumber(null)}
                    className="text-xs font-medium text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    {t('toolManagement.filterClear')}
                  </button>
                )}
              </div>
              <InputNumber
                inputId="filter-tool-number"
                value={filterDraftToolNumber}
                onValueChange={(e) => setFilterDraftToolNumber(e.value ?? null)}
                placeholder={t('toolManagement.filterToolNumberPlaceholder')}
                showButtons={false}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={0}
                min={0}
                className="w-full"
                inputClassName="w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 focus:outline-none focus:border-blue-400 transition-colors"
              />
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* Řazení – segmentované tlačítka */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('toolManagement.sortOrder')}
              </label>
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {(['asc', 'desc'] as const).map((order, i) => (
                  <button
                    key={order}
                    type="button"
                    onClick={() => setFilterDraftOrder(order)}
                    className={[
                      'flex-1 py-2 px-4 text-sm font-medium transition-colors',
                      i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : '',
                      filterDraftOrder === order
                        ? 'bg-blue-500 text-white'
                        : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800',
                    ].join(' ')}
                  >
                    {order === 'asc' ? t('toolManagement.sortAsc') : t('toolManagement.sortDesc')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Dialog>

        <Dialog
          header={t('holders.dimensionsDiagramAlt')}
          visible={showDimensionsModal}
          onHide={() => setShowDimensionsModal(false)}
          className="w-auto max-w-lg"
          pt={TOOL_MANAGEMENT_DIALOG_PT}
        >
          <img
            src="/holder-dimensions.png"
            alt={t('holders.dimensionsDiagramAlt')}
            className="w-full h-auto object-contain"
          />
        </Dialog>

        <Dialog
          header={t('holders.add')}
          visible={showAddHolderDialog}
          onHide={() => setShowAddHolderDialog(false)}
          className="w-full max-w-md"
          pt={TOOL_MANAGEMENT_DIALOG_PT}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                label={t('common.cancel')}
                className="p-button-text"
                onClick={() => setShowAddHolderDialog(false)}
              />
              <Button
                label={t('common.save')}
                loading={addingHolder}
                disabled={addingHolder}
                onClick={handleAddHolder}
              />
            </div>
          }
        >
          <div className="flex flex-col gap-3 pt-2">
            <label className="font-medium text-gray-700">{t('holders.holderName')}</label>
            <InputText
              value={addHolderForm.holder_name ?? ''}
              onChange={(e) => setAddHolderForm((prev) => ({ ...prev, holder_name: e.target.value }))}
              className="w-full"
            />
            <label className="font-medium text-gray-700">{t('holders.diameterMm1')}</label>
            <InputNumber
              value={addHolderForm.diameter_mm_1}
              onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, diameter_mm_1: e.value ?? 0 }))}
              className="w-full"
              mode="decimal"
            />
            <label className="font-medium text-gray-700">{t('holders.diameterMm2')}</label>
            <InputNumber
              value={addHolderForm.diameter_mm_2}
              onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, diameter_mm_2: e.value ?? 0 }))}
              className="w-full"
              mode="decimal"
            />
            <label className="font-medium text-gray-700">{t('holders.lenMm1')}</label>
            <InputNumber
              value={addHolderForm.len_mm_1}
              onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, len_mm_1: e.value ?? 0 }))}
              className="w-full"
              mode="decimal"
            />
            <label className="font-medium text-gray-700">{t('holders.lenMm2')}</label>
            <InputNumber
              value={addHolderForm.len_mm_2}
              onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, len_mm_2: e.value ?? 0 }))}
              className="w-full"
              mode="decimal"
            />
          </div>
        </Dialog>

        <Dialog
          header={t('toolManagement.addDialogTitle')}
          visible={showAddToolDialog}
          onHide={() => setShowAddToolDialog(false)}
          className="w-full max-w-2xl"
          pt={TOOL_MANAGEMENT_DIALOG_PT}
          footer={
            <div className="flex justify-end gap-2 pt-2">
              <Button
                label={t('common.cancel')}
                className="p-button-text"
                onClick={() => setShowAddToolDialog(false)}
              />
              <Button label={t('common.save')} loading={addingTool} disabled={addingTool} onClick={handleAddTool} />
            </div>
          }
        >
          <div className="flex flex-col gap-5 pt-2">
            {/* ── Základní informace ── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                {t('toolManagement.sectionBasic')}
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolName')} *
                  </label>
                  <InputText
                    value={addToolForm.tool_name}
                    onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_name: e.target.value }))}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolType')} *
                  </label>
                  <Dropdown
                    value={addToolForm.tool_type}
                    options={TOOL_TYPE_VALUES.map((v) => ({ label: v, value: v }))}
                    onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_type: e.value as ToolType }))}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.holderName')} *
                  </label>
                  <Dropdown
                    value={addToolForm.holder_id || null}
                    options={holders.map((h) => ({ label: h.holder_name || h.id, value: h.id }))}
                    onChange={(e) => setAddToolForm((prev) => ({ ...prev, holder_id: e.value as string }))}
                    placeholder="—"
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolComment')}
                  </label>
                  <InputText
                    value={addToolForm.tool_comment}
                    onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_comment: e.target.value }))}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1 col-span-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.ballType')}
                  </label>
                  <Dropdown
                    value={ballTypeToDropdown(addToolForm.ball_type)}
                    options={
                      [
                        { label: t('toolManagement.ballTypeUnset'), value: 'unset' },
                        { label: t('common.yes'), value: 'true' },
                        { label: t('common.no'), value: 'false' },
                      ] as { label: string; value: BallTypeDropdownValue }[]
                    }
                    onChange={(e) => {
                      const v = e.value as BallTypeDropdownValue;
                      setAddToolForm((prev) => ({
                        ...prev,
                        ball_type: v === 'true' ? true : v === 'false' ? false : null,
                      }));
                    }}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ── Rozměry ── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                {t('toolManagement.sectionDimensions')}
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolNumber')} *
                  </label>
                  <InputNumber
                    value={addToolForm.tool_number}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_number: e.value ?? 0 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolDiameter')} *
                  </label>
                  <InputNumber
                    value={addToolForm.tool_diameter}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_diameter: e.value ?? 0 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolOverallLen')} *
                  </label>
                  <InputNumber
                    value={addToolForm.tool_overall_len}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_overall_len: e.value ?? 0 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.nonCuttingDiameter')}
                  </label>
                  <InputNumber
                    value={addToolForm.non_cutting_diameter}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, non_cutting_diameter: e.value ?? 0 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolLen')} *
                  </label>
                  <InputNumber
                    value={addToolForm.tool_len}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_len: e.value ?? 0 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolCornerRad')}
                  </label>
                  <InputNumber
                    value={addToolForm.tool_corner_rad}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_corner_rad: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolCutAngle')}
                  </label>
                  <InputNumber
                    value={addToolForm.tool_cut_angle}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_cut_angle: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolCutLen')}
                  </label>
                  <InputNumber
                    value={addToolForm.tool_cut_len}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_cut_len: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.addToolRotation')}
                  </label>
                  <InputText
                    value={addToolForm.tool_rotation}
                    onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_rotation: e.target.value }))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ── Závit / pokročilá geometrie ── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                {t('toolManagement.sectionThread')}
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.pitch')}
                  </label>
                  <InputNumber
                    value={addToolForm.pitch}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, pitch: e.value ?? -1 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.toolPitch')}
                  </label>
                  <InputNumber
                    value={addToolForm.tool_pitch}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_pitch: e.value ?? -1 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.length1')}
                  </label>
                  <InputNumber
                    value={addToolForm.length_1}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, length_1: e.value ?? -1 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.threadDiameter')}
                  </label>
                  <InputNumber
                    value={addToolForm.thread_diameter}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, thread_diameter: e.value ?? -1 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.addDiameter')}
                  </label>
                  <InputNumber
                    value={addToolForm.diameter}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, diameter: e.value ?? -1 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.threadDepth')}
                  </label>
                  <InputNumber
                    value={addToolForm.thread_depth}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, thread_depth: e.value ?? -1 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ── Úhlová geometrie ── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                {t('toolManagement.sectionAngles')}
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.horizontalDistance')}
                  </label>
                  <InputNumber
                    value={addToolForm.horizontal_distance}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, horizontal_distance: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.verticalDistance')}
                  </label>
                  <InputNumber
                    value={addToolForm.vertical_distance}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, vertical_distance: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.tipAngle')}
                  </label>
                  <InputNumber
                    value={addToolForm.tip_angle}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tip_angle: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.flankAngle')}
                  </label>
                  <InputNumber
                    value={addToolForm.flank_angle}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, flank_angle: e.value ?? null }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                    placeholder="—"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ── Parametry obrábění ── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                {t('toolManagement.sectionMachine')}
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.addSpindle')}
                  </label>
                  <InputNumber
                    value={addToolForm.spindle}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, spindle: e.value ?? 0 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    showButtons={false}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('toolManagement.addFeedMachValue')}
                  </label>
                  <InputNumber
                    value={addToolForm.feed_mach_value}
                    onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, feed_mach_value: e.value ?? 20 }))}
                    className="w-full"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    showButtons={false}
                  />
                </div>
                <div className="flex items-center gap-2 pt-4 col-span-2">
                  <Checkbox
                    inputId="addToolSupportFinish"
                    checked={addToolForm.support_finish}
                    onChange={(e) => setAddToolForm((prev) => ({ ...prev, support_finish: e.checked ?? true }))}
                  />
                  <label
                    htmlFor="addToolSupportFinish"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    {t('toolManagement.addSupportFinish')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Dialog>

        {/* ── Tool detail / edit dialog ── */}
        <Dialog
          header={
            detailTool ? `${t('toolManagement.viewTool')}: ${detailTool.tool_name}` : t('toolManagement.viewTool')
          }
          visible={showToolDetailDialog}
          onHide={() => {
            setShowToolDetailDialog(false);
            setDetailEditingField(null);
          }}
          className="w-full max-w-3xl"
          pt={TOOL_MANAGEMENT_DIALOG_PT}
          footer={
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {t('toolManagement.viewDoubleClickHint')}
              </span>
              <div className="flex gap-2">
                <Button
                  label={t('common.cancel')}
                  className="p-button-text"
                  onClick={() => {
                    setShowToolDetailDialog(false);
                    setDetailEditingField(null);
                  }}
                />
                <Button
                  label={t('toolManagement.viewSaveChanges')}
                  loading={detailSaving}
                  disabled={detailSaving || Object.keys(detailEdits).length === 0}
                  onClick={handleDetailSave}
                />
              </div>
            </div>
          }
        >
          {detailLoading || !detailTool ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
              <span>{t('common.loading')}</span>
            </div>
          ) : (
            // onKeyDown zde: Escape ukončí editaci pole, ale NEzavře dialog
            <div
              className="flex flex-col gap-5 pt-2"
              onKeyDown={(e) => {
                if (e.key === 'Escape' && detailEditingField !== null) {
                  e.stopPropagation();
                  setDetailEditingField(null);
                }
              }}
            >
              {/* ── Základní informace ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  {t('toolManagement.sectionBasic')}
                </p>
                <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                  {/* tool_name */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">{t('toolManagement.toolName')}</label>
                    {detailEditingField === 'tool_name' ? (
                      <InputText
                        autoFocus
                        value={detailVal<string>('tool_name') ?? ''}
                        className="w-full"
                        onChange={(e) => setDetailField('tool_name', e.target.value)}
                        onBlur={() => setDetailEditingField(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') setDetailEditingField(null);
                        }}
                      />
                    ) : (
                      <div className="detail-val" onClick={() => setDetailEditingField('tool_name')}>
                        {String(detailVal<string>('tool_name') ?? '—')}
                      </div>
                    )}
                  </div>
                  {/* tool_type — always a Dropdown, selection immediately records the edit */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">{t('toolManagement.toolType')}</label>
                    <Dropdown
                      value={detailVal<ToolType>('tool_type')}
                      options={TOOL_TYPE_VALUES.map((v) => ({ label: v, value: v }))}
                      onChange={(e) => setDetailField('tool_type', e.value)}
                      className="w-full"
                    />
                  </div>
                  {/* holder_id — always a Dropdown, selection immediately records the edit */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">{t('toolManagement.holderName')}</label>
                    <Dropdown
                      value={detailVal<string>('holder_id') || null}
                      options={holders.map((h) => ({ label: h.holder_name || h.id, value: h.id }))}
                      onChange={(e) => setDetailField('holder_id', e.value)}
                      placeholder="—"
                      className="w-full"
                    />
                  </div>
                  {/* tool_number_unique — backend-assigned slot (read-only; not in UpdateTool) */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.toolUniqueNumber')}
                    </label>
                    <div className="text-sm text-gray-800 dark:text-gray-200 py-1 min-h-[2.25rem] flex items-center border-b border-gray-100 dark:border-gray-800">
                      {detailTool.tool_number_unique != null ? String(detailTool.tool_number_unique) : '—'}
                    </div>
                  </div>
                  {/* stack_tool_number — user-assignable stack magazine slot */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.toolStackNumber')}
                    </label>
                    {detailEditingField === 'stack_tool_number' ? (
                      <InputNumber
                        autoFocus
                        value={detailVal<number | null>('stack_tool_number')}
                        onValueChange={(e) => setDetailField('stack_tool_number', e.value ?? null)}
                        onBlur={() => setDetailEditingField(null)}
                        className="w-full"
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={0}
                        showButtons={false}
                      />
                    ) : (
                      <div className="detail-val" onClick={() => setDetailEditingField('stack_tool_number')}>
                        {detailVal<number | null>('stack_tool_number') != null
                          ? String(detailVal<number | null>('stack_tool_number'))
                          : '—'}
                      </div>
                    )}
                  </div>
                  {/* tool_comment */}
                  <div className="flex flex-col gap-1 col-span-3">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.toolComment')}
                    </label>
                    {detailEditingField === 'tool_comment' ? (
                      <InputText
                        autoFocus
                        value={detailVal<string>('tool_comment') ?? ''}
                        className="w-full"
                        onChange={(e) => setDetailField('tool_comment', e.target.value)}
                        onBlur={() => setDetailEditingField(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') setDetailEditingField(null);
                        }}
                      />
                    ) : (
                      <div className="detail-val" onClick={() => setDetailEditingField('tool_comment')}>
                        {String(detailVal<string>('tool_comment') || '—')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800" />

              {/* ── Rozměry ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  {t('toolManagement.sectionDimensions')}
                </p>
                <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                  {(
                    [
                      { field: 'tool_number', label: t('toolManagement.toolNumber'), nullable: false },
                      { field: 'tool_diameter', label: t('toolManagement.toolDiameter'), nullable: false },
                      { field: 'tool_overall_len', label: t('toolManagement.toolOverallLen'), nullable: false },
                      { field: 'non_cutting_diameter', label: t('toolManagement.nonCuttingDiameter'), nullable: false },
                      { field: 'tool_len', label: t('toolManagement.toolLen'), nullable: false },
                      { field: 'tool_corner_rad', label: t('toolManagement.toolCornerRad'), nullable: true },
                      { field: 'tool_cut_angle', label: t('toolManagement.toolCutAngle'), nullable: true },
                      { field: 'tool_cut_len', label: t('toolManagement.toolCutLen'), nullable: true },
                    ] as { field: keyof UpdateTool; label: string; nullable: boolean }[]
                  ).map(({ field, label, nullable }) => (
                    <div key={field} className="flex flex-col gap-1">
                      <label className="text-xs text-gray-500 dark:text-gray-400">{label}</label>
                      {detailEditingField === field ? (
                        <InputNumber
                          autoFocus
                          value={detailVal<number | null>(field)}
                          onValueChange={(e) => setDetailField(field, nullable ? (e.value ?? null) : (e.value ?? 0))}
                          onBlur={() => setDetailEditingField(null)}
                          className="w-full"
                          mode="decimal"
                          minFractionDigits={0}
                          maxFractionDigits={3}
                          showButtons={false}
                          placeholder={nullable ? '—' : undefined}
                        />
                      ) : (
                        <div className="detail-val" onClick={() => setDetailEditingField(field)}>
                          {detailVal<number | null>(field) != null ? String(detailVal<number | null>(field)) : '—'}
                        </div>
                      )}
                    </div>
                  ))}
                  {/* tool_rotation */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.addToolRotation')}
                    </label>
                    {detailEditingField === 'tool_rotation' ? (
                      <InputText
                        autoFocus
                        value={detailVal<string>('tool_rotation') ?? ''}
                        className="w-full"
                        onChange={(e) => setDetailField('tool_rotation', e.target.value)}
                        onBlur={() => setDetailEditingField(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') setDetailEditingField(null);
                        }}
                      />
                    ) : (
                      <div className="detail-val" onClick={() => setDetailEditingField('tool_rotation')}>
                        {String(detailVal<string>('tool_rotation') ?? '—')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800" />

              {/* ── Závit / pokročilá geometrie ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  {t('toolManagement.sectionThread')}
                </p>
                <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                  {(
                    [
                      { field: 'pitch', label: t('toolManagement.pitch') },
                      { field: 'tool_pitch', label: t('toolManagement.toolPitch') },
                      { field: 'length_1', label: t('toolManagement.length1') },
                      { field: 'thread_diameter', label: t('toolManagement.threadDiameter') },
                      { field: 'diameter', label: t('toolManagement.addDiameter') },
                      { field: 'thread_depth', label: t('toolManagement.threadDepth') },
                    ] as { field: keyof UpdateTool; label: string }[]
                  ).map(({ field, label }) => (
                    <div key={field} className="flex flex-col gap-1">
                      <label className="text-xs text-gray-500 dark:text-gray-400">{label}</label>
                      {detailEditingField === field ? (
                        <InputNumber
                          autoFocus
                          value={detailVal<number>(field)}
                          onValueChange={(e) => setDetailField(field, e.value ?? -1)}
                          onBlur={() => setDetailEditingField(null)}
                          className="w-full"
                          mode="decimal"
                          minFractionDigits={0}
                          maxFractionDigits={3}
                          showButtons={false}
                        />
                      ) : (
                        <div className="detail-val" onClick={() => setDetailEditingField(field)}>
                          {detailVal<number>(field) != null ? String(detailVal<number>(field)) : '—'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800" />

              {/* ── Úhlová geometrie ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  {t('toolManagement.sectionAngles')}
                </p>
                <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                  {(
                    [
                      { field: 'horizontal_distance', label: t('toolManagement.horizontalDistance') },
                      { field: 'vertical_distance', label: t('toolManagement.verticalDistance') },
                      { field: 'tip_angle', label: t('toolManagement.tipAngle') },
                      { field: 'flank_angle', label: t('toolManagement.flankAngle') },
                    ] as { field: keyof UpdateTool; label: string }[]
                  ).map(({ field, label }) => (
                    <div key={field} className="flex flex-col gap-1">
                      <label className="text-xs text-gray-500 dark:text-gray-400">{label}</label>
                      {detailEditingField === field ? (
                        <InputNumber
                          autoFocus
                          value={detailVal<number | null>(field)}
                          onValueChange={(e) => setDetailField(field, e.value ?? null)}
                          onBlur={() => setDetailEditingField(null)}
                          className="w-full"
                          mode="decimal"
                          minFractionDigits={0}
                          maxFractionDigits={3}
                          showButtons={false}
                          placeholder="—"
                        />
                      ) : (
                        <div className="detail-val" onClick={() => setDetailEditingField(field)}>
                          {detailVal<number | null>(field) != null ? String(detailVal<number | null>(field)) : '—'}
                        </div>
                      )}
                    </div>
                  ))}
                  {/* ball_type — tri-state; lives in angle-geometry grid per UX */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.ballType')}
                    </label>
                    <Dropdown
                      value={ballTypeToDropdown(detailVal<boolean | null>('ball_type'))}
                      options={
                        [
                          { label: t('toolManagement.ballTypeUnset'), value: 'unset' },
                          { label: t('common.yes'), value: 'true' },
                          { label: t('common.no'), value: 'false' },
                        ] as { label: string; value: BallTypeDropdownValue }[]
                      }
                      onChange={(e) => {
                        const v = e.value as BallTypeDropdownValue;
                        setDetailField(
                          'ball_type',
                          v === 'true' ? true : v === 'false' ? false : null
                        );
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800" />

              {/* ── Parametry obrábění ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  {t('toolManagement.sectionMachine')}
                </p>
                <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                  {/* spindle */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">{t('toolManagement.addSpindle')}</label>
                    {detailEditingField === 'spindle' ? (
                      <InputNumber
                        autoFocus
                        value={detailVal<number>('spindle')}
                        onValueChange={(e) => setDetailField('spindle', e.value ?? 0)}
                        onBlur={() => setDetailEditingField(null)}
                        className="w-full"
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={0}
                        showButtons={false}
                      />
                    ) : (
                      <div className="detail-val" onClick={() => setDetailEditingField('spindle')}>
                        {String(detailVal<number>('spindle') ?? '—')}
                      </div>
                    )}
                  </div>
                  {/* feed_mach_value */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.addFeedMachValue')}
                    </label>
                    {detailEditingField === 'feed_mach_value' ? (
                      <InputNumber
                        autoFocus
                        value={detailVal<number>('feed_mach_value')}
                        onValueChange={(e) => setDetailField('feed_mach_value', e.value ?? 20)}
                        onBlur={() => setDetailEditingField(null)}
                        className="w-full"
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={3}
                        showButtons={false}
                      />
                    ) : (
                      <div className="detail-val" onClick={() => setDetailEditingField('feed_mach_value')}>
                        {String(detailVal<number>('feed_mach_value') ?? '—')}
                      </div>
                    )}
                  </div>
                  {/* support_finish */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      {t('toolManagement.addSupportFinish')}
                    </label>
                    <div className="flex items-center gap-2 py-1">
                      <Checkbox
                        inputId="detailSupportFinish"
                        checked={detailVal<boolean>('support_finish') ?? false}
                        onChange={(e) => setDetailField('support_finish', e.checked ?? false)}
                      />
                      <label
                        htmlFor="detailSupportFinish"
                        className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
                      >
                        {detailVal<boolean>('support_finish') ? t('common.yes') : t('common.no')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Dialog>

        <PredictToolsDialog visible={showPredictDialog} onHide={() => setShowPredictDialog(false)} />
      </div>
    </div>
  );
};
